import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);

  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [prices, setPrices] = useState({});
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);

  const quickBuy = location.state?.quickBuy;
  const giftCardId = location.state?.giftCardId;
  const denomination = location.state?.denomination;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
    loadPrices();
    loadWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadPrices = async () => {
    try {
      const res = await axios.get('/api/wallet/prices');
      setPrices(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadWallets = async () => {
    try {
      const res = await axios.get('/api/wallet/balances');
      setWallets(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateOrder = async () => {
    try {
      setLoading(true);

      let res;
      if (quickBuy) {
        res = await axios.post('/api/orders/quick-buy', {
          giftCardId,
          denomination,
          cryptocurrency: selectedCrypto
        });
      } else {
        res = await axios.post('/api/orders/create', {
          cryptocurrency: selectedCrypto,
          denomination: cart[0]?.giftCard?.denominations[0]?.value || 50
        });
      }

      if (!quickBuy) {
        await clearCart();
      }

      alert(res.data.msg || 'Order created successfully! Funds transferred. Awaiting fulfillment.');
      navigate('/dashboard');
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to create order. Please check your balance.');
    }
    setLoading(false);
  };

  const selectedWallet = wallets.find(w => w.cryptocurrency === selectedCrypto);
  const totalUSD = quickBuy ? 0 : getCartTotal();
  const cryptoAmount = prices[selectedCrypto] ? (totalUSD / prices[selectedCrypto]).toFixed(8) : '0';
  const hasEnoughBalance = selectedWallet && selectedWallet.balance >= parseFloat(cryptoAmount);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-4xl font-bold mb-8">Checkout</h1>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold mb-6">Select Payment Method</h2>

        {/* Crypto Selection */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {['BTC', 'ETC', 'LTC', 'DOGE'].map((crypto) => {
            const wallet = wallets.find(w => w.cryptocurrency === crypto);
            return (
              <button
                key={crypto}
                onClick={() => setSelectedCrypto(crypto)}
                className={`p-4 border-2 rounded-lg transition ${
                  selectedCrypto === crypto
                    ? 'border-primary bg-blue-50'
                    : 'border-gray-300 hover:border-primary'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">
                    {crypto === 'BTC' && '₿'}
                    {crypto === 'ETC' && 'ΞC'}
                    {crypto === 'LTC' && 'Ł'}
                    {crypto === 'DOGE' && 'Ð'}
                  </div>
                  <div className="font-semibold">{crypto}</div>
                  {prices[crypto] && (
                    <div className="text-sm text-gray-600">${prices[crypto].toFixed(2)}</div>
                  )}
                  {wallet && (
                    <div className="text-xs text-green-600 mt-1">
                      Balance: {wallet.balance.toFixed(8)}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
          {!quickBuy && cart.map((item) => (
            <div key={item.giftCard._id} className="flex justify-between mb-2">
              <span>{item.giftCard.name} x {item.quantity}</span>
              <span>${(item.giftCard.denominations[0]?.priceUSD * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between text-lg font-bold mb-2">
              <span>Total (USD)</span>
              <span>${totalUSD.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-primary mb-2">
              <span>Amount ({selectedCrypto})</span>
              <span>{cryptoAmount}</span>
            </div>
            {selectedWallet && (
              <div className="flex justify-between text-sm mt-2">
                <span>Your Balance:</span>
                <span className={hasEnoughBalance ? 'text-green-600' : 'text-red-600'}>
                  {selectedWallet.balance.toFixed(8)} {selectedCrypto}
                </span>
              </div>
            )}
          </div>
        </div>

        {!hasEnoughBalance && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">
              <strong>Insufficient balance!</strong> Please deposit {selectedCrypto} to your platform wallet first.
              Go to your <button onClick={() => navigate('/dashboard')} className="underline">Dashboard</button> to see your deposit address.
            </p>
          </div>
        )}

        {hasEnoughBalance && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-700 text-sm">
              <strong>How it works:</strong> Your funds will be transferred to the platform wallet. The admin will then fulfill your order with gift card codes that will appear in your dashboard.
            </p>
          </div>
        )}

        <button
          onClick={handleCreateOrder}
          disabled={loading || !hasEnoughBalance}
          className="w-full bg-primary text-white py-3 rounded-md hover:bg-blue-600 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Processing...' : hasEnoughBalance ? 'Place Order' : 'Insufficient Balance'}
        </button>
      </div>
    </div>
  );
};

export default Checkout;
